import {
  hideHeaderOverlayScrim,
  isHeaderOverlayScrimTarget,
  showHeaderOverlayScrim,
} from '@theme/header-overlay-scrim';

/** @type {number} */
const OPEN_DELAY_MS = 120;

/** @type {number} */
const CLOSE_DELAY_MS = 200;

/**
 * Desktop Customer Care dropdown — opens on hover/focus, closes on leave, scrim click, or Escape.
 */
class HeaderCustomerCare extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {number | undefined} */
  #openTimer;

  /** @type {number | undefined} */
  #closeTimer;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#trigger = this.querySelector('.header-customer-care__trigger');
    this.#panel = this.querySelector('.header-customer-care__panel');

    if (!this.#trigger || !this.#panel) return;

    this.#trigger.addEventListener(
      'click',
      (event) => {
        if (window.matchMedia('(min-width: 750px)').matches) {
          event.preventDefault();
        }
      },
      { signal }
    );

    this.addEventListener('mouseenter', this.#scheduleOpen, { signal });
    this.addEventListener('mouseleave', this.#scheduleClose, { signal });
    this.addEventListener('focusin', this.#scheduleOpen, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    document.addEventListener(
      'click',
      (event) => {
        if (this.dataset.open !== 'true') return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (target instanceof Element && isHeaderOverlayScrimTarget(target)) {
          this.#setOpen(false);
          return;
        }
        if (this.contains(target)) return;
        this.#setOpen(false);
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.dataset.open === 'true') {
          event.preventDefault();
          this.#setOpen(false);
          this.#trigger?.focus();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (this.dataset.open === 'true') {
      hideHeaderOverlayScrim();
    }
    this.#abortController?.abort();
    this.#clearTimers();
  }

  #scheduleOpen = () => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    this.#clearCloseTimer();
    this.#clearOpenTimer();
    this.#openTimer = window.setTimeout(() => this.#setOpen(true), OPEN_DELAY_MS);
  };

  #scheduleClose = () => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    this.#clearOpenTimer();
    this.#clearCloseTimer();
    this.#closeTimer = window.setTimeout(() => this.#setOpen(false), CLOSE_DELAY_MS);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const related = event.relatedTarget;
    if (related instanceof Node && this.contains(related)) return;

    this.#scheduleClose();
  };

  /** @param {boolean} open */
  #setOpen(open) {
    this.#clearTimers();

    if (open) {
      this.dataset.open = 'true';
      showHeaderOverlayScrim();
      this.#trigger?.setAttribute('aria-expanded', 'true');
      this.#panel?.removeAttribute('hidden');
    } else {
      delete this.dataset.open;
      hideHeaderOverlayScrim();
      this.#trigger?.setAttribute('aria-expanded', 'false');
      this.#panel?.setAttribute('hidden', '');
    }
  }

  #clearOpenTimer() {
    if (this.#openTimer !== undefined) {
      window.clearTimeout(this.#openTimer);
      this.#openTimer = undefined;
    }
  }

  #clearCloseTimer() {
    if (this.#closeTimer !== undefined) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = undefined;
    }
  }

  #clearTimers() {
    this.#clearOpenTimer();
    this.#clearCloseTimer();
  }
}

if (!customElements.get('header-customer-care')) {
  customElements.define('header-customer-care', HeaderCustomerCare);
}
